import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
  IconExternalLink,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import type { ProductListingGroupSort } from "@/lib/product-listing-api";
import { cn } from "@/lib/utils";
import { amazonProductUrl } from "./product-listing-helpers";

export function ProductRowsSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col gap-4 p-4">
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex gap-3">
          <Skeleton className="size-12 shrink-0" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function PaneHeading({
  title,
  children,
  compact = false,
}: {
  title: string;
  children?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between border-b border-line",
        compact ? "h-[31px] bg-surface px-3.5" : "h-11 bg-subtle px-3",
      )}
    >
      <h4 className="text-xs font-semibold">{title}</h4>
      {children}
    </div>
  );
}

export function ProductListingSortHeader({
  field,
  label,
  sort,
  onSort,
}: {
  field: ProductListingGroupSort["sortBy"];
  label: string;
  sort: ProductListingGroupSort | null;
  onSort: (field: ProductListingGroupSort["sortBy"]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const active = sort?.sortBy === field;
  const nextOrder = active && sort.sortOrder === "asc" ? "desc" : "asc";
  const SortIcon = !active
    ? IconArrowsSort
    : sort.sortOrder === "asc"
      ? IconArrowUp
      : IconArrowDown;
  return (
    <button
      type="button"
      aria-label={t(`products.table.sort${nextOrder === "asc" ? "Ascending" : "Descending"}`, {
        column: label,
      })}
      aria-pressed={active}
      className={cn(
        "flex h-6 items-center gap-1 rounded px-1 text-left outline-none transition-colors hover:bg-subtle focus-visible:ring-2 focus-visible:ring-ring",
        active && "text-foreground",
      )}
      onClick={() => onSort(field)}
    >
      <span>{label}</span>
      <SortIcon className="size-3 shrink-0" stroke={1.7} aria-hidden />
    </button>
  );
}

export function AmazonProductLink({
  asin,
  marketplaceId,
}: {
  asin: string;
  marketplaceId: string;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <a
      href={amazonProductUrl(asin, marketplaceId)}
      target="_blank"
      rel="noreferrer"
      className="inline-flex shrink-0 items-center text-primary hover:text-primary/80 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={t("products.openAmazon", { asin })}
      title={t("products.openAmazon", { asin })}
      onClick={(event) => event.stopPropagation()}
    >
      <IconExternalLink className="size-3.5" />
    </a>
  );
}

export function PaginationFooter({
  page,
  total,
  pageSize,
  onPageChange,
}: {
  page: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div className="mt-auto border-t border-line p-2">
      <DataTablePagination
        page={page}
        pageCount={Math.max(1, Math.ceil(total / pageSize))}
        summary={t("products.pagination", {
          from: total === 0 ? 0 : (page - 1) * pageSize + 1,
          to: Math.min(total, page * pageSize),
          total,
        })}
        onPageChange={onPageChange}
      />
    </div>
  );
}

export function EligibilityCheckRetry({
  pending,
  onRetry,
}: {
  pending: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div
      role="alert"
      aria-busy={pending}
      className="mt-1.5 flex flex-col items-start gap-2 text-xs"
    >
      <p className="flex items-start gap-1.5 leading-[18px] text-muted-foreground">
        <IconAlertTriangle
          className="mt-0.5 size-3.5 shrink-0 text-warning-foreground"
          aria-hidden
        />
        <span>{t("products.adEligibility.notReturned")}</span>
      </p>
      <Button type="button" variant="outline" size="xs" disabled={pending} onClick={onRetry}>
        {pending ? <Spinner data-icon="inline-start" /> : null}
        {t(pending ? "products.selectionCheck.checking" : "actions.retry")}
      </Button>
    </div>
  );
}
